import { z } from '@repo/schema'
import { getDataWrapperSchema } from '@repo/schema/data-wrapper'
import { GetPaginationSchema, PaginationInputSchema } from '@repo/schema/pagination'
import { GetRoleWithTimestampsSchema } from '@repo/schema/role'
import { GetStatusSchema } from '@repo/schema/status'
import { GetLastAuthSchema, GetUserSchema } from '@repo/schema/user'
import { UpFetch } from '@repo/upfetch'

import { createProcedure } from '../../lib/procedure.ts'

export const createRolesRoute = (upFetch: UpFetch) => {
  const p = createProcedure(upFetch)

  return {
    attachPermission: p
      .path(z.object({ roleId: z.number() }), p => ['roles', p.roleId, 'permissions', 'attach'])
      .input(z.object({ permission: z.string() }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(z.object({ id: z.number(), name: z.string() })),
        }),
      ),
    attachToUser: p
      .path(z.object({ userId: z.number() }), p => ['users', p.userId, 'roles', 'attach'])
      .input(z.object({ role: z.string() }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: z.object({ data: z.array(z.object({ id: z.number(), name: z.string() })) }),
        }),
      ),
    bulkAttachUsers: p
      .path(z.object({ roleId: z.number() }), p => ['roles', p.roleId, 'users', 'attach'])
      .input(z.object({ users: z.array(z.number()) }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: GetPaginationSchema(GetUserSchema),
        }),
      ),
    create: p
      .path('roles')
      .input(z.object({ name: z.string() }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, { body: { ...input, guard_name: 'api' }, method: 'POST' }),
      ),
    delete: p
      .path(z.object({ roleId: z.number() }), p => ['roles', p.roleId])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),
    detachFromUser: p
      .path(z.object({ userId: z.number() }), p => ['users', p.userId, 'roles', 'detach'])
      .input(z.object({ role: z.string() }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: z.object({ data: z.array(z.object({ id: z.number(), name: z.string() })) }),
        }),
      ),
    detachPermission: p
      .path(z.object({ roleId: z.number() }), p => ['roles', p.roleId, 'permissions', 'detach'])
      .input(z.object({ permission: z.string() }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(z.object({ id: z.number(), name: z.string() })),
        }),
      ),
    role: {
      details: p
        .path(z.object({ id: z.number() }), p => ['roles', p.id])
        .query(({ path, upFetch }) =>
          upFetch(path, {
            schema: getDataWrapperSchema(GetRoleWithTimestampsSchema),
          }),
        ),
      permissions: p
        .path(z.object({ id: z.number() }), p => ['roles', p.id, 'permissions'])
        .query(({ path, upFetch }) =>
          upFetch(path, {
            schema: getDataWrapperSchema(
              z
                .object({
                  id: z.number(),
                  name: z.string(),
                })
                .array(),
            ),
          }),
        ),
      users: p
        .path(z.object({ id: z.number() }), p => ['roles', p.id, 'users'])
        .input(PaginationInputSchema)
        .query(({ input, path, upFetch }) =>
          upFetch(path, {
            params: {
              ...input,
              i: ['status', 'last_auth'].join('|'),
            },
            schema: GetPaginationSchema(
              GetUserSchema.extend({
                last_auth: GetLastAuthSchema.nullish(),
                status: GetStatusSchema.nullish(),
              }),
            ),
          }),
        ),
    },
    roles: p
      .path('roles')
      .input(PaginationInputSchema)
      .query(({ input, path, upFetch }) =>
        upFetch(path, {
          params: input,
          schema: GetPaginationSchema(GetRoleWithTimestampsSchema),
        }),
      ),
    syncPermissions: p
      .path(z.object({ roleId: z.number() }), p => ['roles', p.roleId, 'permissions', 'sync'])
      .input(z.object({ permissions: z.string().array() }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(z.object({ id: z.number(), name: z.string() })),
        }),
      ),
    update: p
      .path(z.object({ roleId: z.number() }), p => ['roles', p.roleId])
      .input(z.object({ name: z.string() }))
      .mutation(({ input, path, upFetch }) => upFetch(path, { body: input, method: 'PATCH' })),
  }
}
